import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import PostCard from "../components/PostCard";
import VerifiedBadge from "../components/VerifiedBadge";
import { Shield } from "lucide-react";

export default function TeamPage() {
  const { teamName } = useParams();
  const [team, setTeam] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTeam(null);
    setNotFound(false);
    api
      .get(`/teams/${encodeURIComponent(teamName)}`)
      .then(({ data }) => setTeam(data))
      .catch(() => setNotFound(true));
  }, [teamName]);

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-[#a3a3a3]" data-testid="team-not-found">
        No team called “{teamName}” on EGNCY yet.
      </div>
    );
  }
  if (!team) {
    return <div className="max-w-4xl mx-auto p-8 text-[#737373]">Loading…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="team-page">
      <div className="bg-[#121212] border border-[#262626] rounded-sm p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#0a0a0a] border border-[#404040] rounded-sm flex items-center justify-center shrink-0">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1
              data-testid="team-name"
              className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white leading-none"
            >
              {team.team}
            </h1>
            <div className="text-sm text-[#a3a3a3] mt-1">
              {team.roster.length} {team.roster.length === 1 ? "member" : "members"} on EGNCY
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6">
          <StatCell label="Matches" value={team.career.matches} testid="team-total-matches" />
          <StatCell label="Goals" value={team.career.goals} testid="team-total-goals" />
          <StatCell label="Assists" value={team.career.assists} testid="team-total-assists" />
          <StatCell label="Mins" value={team.career.minutes} testid="team-total-mins" />
        </div>
      </div>

      {team.seasons.length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-sm p-5" data-testid="team-season-stats">
          <h2 className="font-display text-xl uppercase tracking-widest text-white mb-4">
            Season Stats
          </h2>
          <div className="space-y-2">
            <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-widest text-[#737373] px-3">
              <div>Season</div>
              <div className="text-center">Matches</div>
              <div className="text-center">Goals</div>
              <div className="text-center">Assists</div>
              <div className="text-center">Mins</div>
            </div>
            {team.seasons.map((s) => (
              <div
                key={s.season}
                className="grid grid-cols-5 gap-2 items-center bg-[#0a0a0a] border border-[#262626] px-3 py-3 rounded-sm"
              >
                <div className="font-display text-lg text-white leading-none">{s.season}</div>
                <div className="text-center font-display text-lg text-white">{s.matches}</div>
                <div className="text-center font-display text-lg text-white">{s.goals}</div>
                <div className="text-center font-display text-lg text-white">{s.assists}</div>
                <div className="text-center font-display text-lg text-white">{s.minutes}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#121212] border border-[#262626] rounded-sm" data-testid="team-roster">
        <div className="px-5 py-3 border-b border-[#262626]">
          <h2 className="font-display text-xl uppercase tracking-widest text-white">Roster</h2>
        </div>
        <ul className="divide-y divide-[#262626]">
          {team.roster.map((p) => (
            <li key={p.id} data-testid={`roster-player-${p.id}`} className="flex items-center gap-3 p-4">
              <Link to={`/profile/${p.id}`}>
                <Avatar url={p.avatar_url} name={p.name} size={44} />
              </Link>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/profile/${p.id}`}
                  className="text-white font-semibold text-sm hover:underline inline-flex items-center gap-1.5"
                >
                  {p.name}
                  <VerifiedBadge role={p.role} verified={p.verified} size={13} />
                </Link>
                <div className="text-xs text-[#a3a3a3]">
                  {p.role && p.role !== "player"
                    ? p.role.charAt(0).toUpperCase() + p.role.slice(1)
                    : p.position || "Player"}
                  {" · "}
                  {p.followers_count} followers
                </div>
              </div>
              <Link
                to={`/profile/${p.id}`}
                className="text-xs px-3 py-1.5 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
              >
                View
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <h2 className="font-display text-xl uppercase tracking-widest text-white pt-2">
        Team Highlights
      </h2>
      {team.posts.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-8 text-center rounded-sm text-[#a3a3a3]">
          No matches posted yet.
        </div>
      ) : (
        <div className="space-y-4">
          {team.posts.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#262626] p-3 text-center rounded-sm">
      <div data-testid={testid} className="font-display text-2xl text-white leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{label}</div>
    </div>
  );
}
